/**
 * The words on the workspace-settings switches, shared by the Settings pages and the setup panel's
 * "Tweak your workspace settings" step (ACT2-1108), so the two can never describe a switch differently.
 */
import type { RouterClient } from '@orpc/server';
import type { AppRouter } from '@server/rpc';

export type WorkspaceSettings = Awaited<ReturnType<RouterClient<AppRouter>['settings']['get']>>['settings'];

export type AiControlKey = keyof Pick<
  WorkspaceSettings,
  'useTeamNotesInAi' | 'useGeneratedContextInAi' | 'allowChatMemoryCapture' | 'requireMemoryApproval'
>;

export const AI_CONTROLS: { key: AiControlKey; title: string; description: string }[] = [
  {
    key: 'useTeamNotesInAi',
    title: 'Use team notes and memories in AI',
    description: 'Allow manually-authored notes and saved memories to be retrieved in chat.',
  },
  {
    key: 'useGeneratedContextInAi',
    title: 'Use generated context in AI',
    description: 'Let the agent retrieve generated docs and warehouse-derived context when relevant.',
  },
  {
    key: 'allowChatMemoryCapture',
    title: 'Allow chat memory capture',
    description: 'Let the agent save durable facts from chats as visible, editable memory items.',
  },
  {
    key: 'requireMemoryApproval',
    title: 'Require memory approval',
    description: 'Keep new AI memories out of agent retrieval until an admin approves them.',
  },
];

export const SLACK_GUEST_ACCESS = {
  title: 'Guest access',
  description:
    "Let Slack users who haven't connected a Lookout workspace ask read-only questions in connected channels and DMs.",
} as const;

export const PUBLIC_SHARING = {
  title: 'Allow dashboards to be shared publicly?',
  description:
    "Anyone with a public URL will be able to view the dashboard without signing in. When this is off, existing public links won't work.",
} as const;
